// solo/Bookings.jsx
// Session 31 changes:
//   - Checkout button navigates to /solo/record-sale/:bookingId (new full till)
//   - Cancel appointment requires confirmation dialog before firing
//   - Old inline checkout dialog removed
//
// Day-location V1 (Aug 2026):
//   - Fetches day_locations from /solo/booking-settings once on mount
//   - Shows the day's location label under each day column header via
//     FullCalendar's dayHeaderContent, so it appears in week, day, and
//     month views without needing separate handling per view.
//   - Purely a display label — no change to appointment data, income,
//     or booking logic.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Chip,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  PersonAdd as PersonAddIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../axiosConfig';
import JobCard from './JobCard';
import RetailRecommendations from './RetailRecommendations';

// Day-location V1: JS Date.getDay() returns 0=Sunday..6=Saturday, so this
// array is indexed to match that directly (index 0 = 'sun').
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const Bookings = () => {
  const calendarRef = useRef(null);
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Day-location V1 — { mon: 'Salon A', tue: '', ... } from booking_settings
  const [dayLocations, setDayLocations] = useState({});

  // Job card
  const [jobCardOpen, setJobCardOpen] = useState(false);
  const [jobCardAppointment, setJobCardAppointment] = useState(null);

  // Client autocomplete
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientInputValue, setClientInputValue] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientFields, setShowNewClientFields] = useState(false);

  // Booking form
  const [formData, setFormData] = useState({
    client_id: null,
    client_name: '',
    client_phone: '',
    client_email: '',
    appointment_date: '',
    start_time: '09:00',
    notes: '',
    is_multi_service: false,
    services: []
  });

  const [currentService, setCurrentService] = useState({
    service_id: '',
    service_name: '',
    duration: 0,
    processing_time: 0,
    price: 0,
    requires_patch_test: false
  });

  const [formErrors, setFormErrors] = useState({});
  const [currentDateRange, setCurrentDateRange] = useState(null);
  const [patchTestWarning, setPatchTestWarning] = useState(null);

  // ── Helpers ────────────────────────────────────────────────
  const calculateAbsoluteTime = (baseTime, offsetMinutes) => {
    const [hours, minutes] = baseTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes + offsetMinutes);
    return date.toTimeString().substring(0, 5);
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'primary',
      completed: 'success',
      cancelled: 'default',
      no_show: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusColorHex = (status) => {
    const colors = {
      scheduled: '#2196F3',
      completed: '#4CAF50',
      cancelled: '#9E9E9E',
      no_show: '#F44336'
    };
    return colors[status] || '#2196F3';
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // ── Data fetching ──────────────────────────────────────────
  const fetchAppointments = async (startDate = null, endDate = null) => {
    try {
      setLoading(true);
      const calendarApi = calendarRef.current?.getApi();

      let params = {};
      if (startDate && endDate) {
        params = { start: startDate, end: endDate };
      } else if (calendarApi) {
        const view = calendarApi.view;
        params = {
          start: view.activeStart.toISOString(),
          end: view.activeEnd.toISOString()
        };
      } else {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
        params = { start: start.toISOString(), end: end.toISOString() };
      }

      const response = await api.get('/appointments', { params });
      const appointments = response.data || [];
      const calendarEvents = [];

      appointments.forEach(appt => {
        const apptData = appt.extendedProps || appt;

        if (apptData.is_multi_service && apptData.services && apptData.services.length > 0) {
          apptData.services.forEach((svc, index) => {
            const serviceStart = calculateAbsoluteTime(apptData.start_time, svc.start_offset);
            const serviceEnd = calculateAbsoluteTime(
              apptData.start_time,
              svc.start_offset + svc.duration
            );

            calendarEvents.push({
              id: `${apptData.id}-svc-${index}`,
              title: `${apptData.client_name} - ${svc.service_name}`,
              start: `${apptData.appointment_date}T${serviceStart}`,
              end: `${apptData.appointment_date}T${serviceEnd}`,
              backgroundColor: getStatusColorHex(apptData.status),
              borderColor: getStatusColorHex(apptData.status),
              extendedProps: {
                ...apptData,
                currentService: svc,
                isMultiService: true,
                serviceIndex: index
              }
            });

            if (svc.processing_time > 0) {
              const processingEnd = calculateAbsoluteTime(
                apptData.start_time,
                svc.start_offset + svc.duration + svc.processing_time
              );
              calendarEvents.push({
                id: `${apptData.id}-processing-${index}`,
                title: `${apptData.client_name} - Processing`,
                start: `${apptData.appointment_date}T${serviceEnd}`,
                end: `${apptData.appointment_date}T${processingEnd}`,
                backgroundColor: '#e0e0e0',
                borderColor: '#bdbdbd',
                display: 'background',
                extendedProps: { ...apptData, isProcessing: true, serviceIndex: index }
              });
            }
          });
        } else {
          calendarEvents.push({
            id: apptData.id,
            title: appt.title || `${apptData.client_name}${apptData.service_name ? ' - ' + apptData.service_name : ''}`,
            start: appt.start,
            end: appt.end,
            backgroundColor: appt.backgroundColor || getStatusColorHex(apptData.status),
            borderColor: appt.borderColor || getStatusColorHex(apptData.status),
            extendedProps: apptData
          });
        }
      });

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      showSnackbar('Failed to load appointments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  // Day-location V1 — fetch once on mount. Silently no-ops on failure
  // (e.g. row not yet created) since the header just shows nothing extra.
  const fetchDayLocations = async () => {
    try {
      const response = await api.get('/solo/booking-settings');
      setDayLocations(response.data?.day_locations || {});
    } catch (error) {
      console.error('Error fetching day locations:', error);
    }
  };

  const searchClients = async (query) => {
    if (!query || query.length < 2) { setClientOptions([]); return; }
    try {
      setClientLoading(true);
      const response = await api.get('/clients/search', { params: { q: query } });
      setClientOptions(response.data || []);
    } catch (error) {
      console.error('Error searching clients:', error);
      setClientOptions([]);
    } finally {
      setClientLoading(false);
    }
  };

  useEffect(() => { fetchServices(); }, []);
  useEffect(() => { fetchDayLocations(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientInputValue && !selectedClient) searchClients(clientInputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientInputValue]);

  useEffect(() => {
    if (selectedClient) {
      const status = selectedClient.patch_test_status;
      if (status === 'none') setPatchTestWarning('This client has no patch test on record');
      else if (status === 'expired') setPatchTestWarning("This client's patch test has expired");
      else if (status === 'positive') setPatchTestWarning('This client had a positive reaction - DO NOT book colour services');
      else if (status === 'pending') setPatchTestWarning('Patch test pending - wait 48 hours before booking colour');
      else setPatchTestWarning(null);
    } else {
      setPatchTestWarning(null);
    }
  }, [selectedClient]);

  // ── Dialog open/close ──────────────────────────────────────
  const handleOpenDialog = (date = null, time = null) => {
    const now = new Date();
    setFormData({
      client_id: null,
      client_name: '',
      client_phone: '',
      client_email: '',
      appointment_date: date || now.toISOString().split('T')[0],
      start_time: time || '09:00',
      notes: '',
      is_multi_service: false,
      services: []
    });
    setCurrentService({ service_id: '', service_name: '', duration: 0, processing_time: 0, price: 0, requires_patch_test: false });
    setFormErrors({});
    setSelectedEvent(null);
    setSelectedClient(null);
    setClientInputValue('');
    setClientOptions([]);
    setShowNewClientFields(false);
    setPatchTestWarning(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({ client_id: null, client_name: '', client_phone: '', client_email: '', appointment_date: '', start_time: '09:00', notes: '', is_multi_service: false, services: [] });
    setCurrentService({ service_id: '', service_name: '', duration: 0, processing_time: 0, price: 0, requires_patch_test: false });
    setFormErrors({});
    setSelectedClient(null);
    setClientInputValue('');
    setShowNewClientFields(false);
    setPatchTestWarning(null);
  };

  // ── Service ID memo (retail recommendations) ───────────────
  const selectedServiceIds = useMemo(() => {
    if (!selectedEvent) return [];
    if (Array.isArray(selectedEvent.services) && selectedEvent.services.length > 0) {
      return selectedEvent.services.map(s => s.service_id).filter(id => Number.isInteger(id));
    }
    if (Number.isInteger(selectedEvent.service_id)) return [selectedEvent.service_id];
    return [];
  }, [selectedEvent]);

  // ── Multi-service helpers ──────────────────────────────────
  const calculateServiceStartTime = (serviceIndex) => {
    if (!formData.start_time) return '--:--';
    let offset = 0;
    for (let i = 0; i < serviceIndex; i++) {
      const svc = formData.services[i];
      offset += svc.duration + svc.processing_time;
    }
    return calculateAbsoluteTime(formData.start_time, offset);
  };

  const calculateTotalDuration = () =>
    formData.services.reduce((total, svc) => total + svc.duration + svc.processing_time, 0);

  const calculateTotalPrice = () =>
    formData.services.reduce((total, svc) => total + parseFloat(svc.price || 0), 0);

  const calculateEndTime = () => {
    if (!formData.start_time) return '--:--';
    return calculateAbsoluteTime(formData.start_time, calculateTotalDuration());
  };

  const hasServiceRequiringPatchTest = () =>
    formData.services.some(svc => svc.requires_patch_test);

  const handleServiceSelect = (e) => {
    const service = services.find(s => s.id === parseInt(e.target.value));
    if (service) {
      setCurrentService({
        service_id: service.id,
        service_name: service.name,
        duration: service.duration || 30,
        processing_time: service.processing_time || 0,
        price: service.default_price || 0,
        requires_patch_test: service.requires_patch_test || false
      });
    }
  };

  const addService = () => {
    if (!currentService.service_id) return;
    setFormData(prev => ({ ...prev, is_multi_service: true, services: [...prev.services, { ...currentService }] }));
    setCurrentService({ service_id: '', service_name: '', duration: 0, processing_time: 0, price: 0, requires_patch_test: false });
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
      is_multi_service: prev.services.length > 1
    }));
  };

  // ── Client selection ───────────────────────────────────────
  const handleClientSelect = (event, value) => {
    if (value) {
      setSelectedClient(value);
      setFormData(prev => ({ ...prev, client_id: value.id, client_name: value.full_name, client_phone: value.phone || '', client_email: value.email || '' }));
      setShowNewClientFields(false);
    } else {
      setSelectedClient(null);
      setFormData(prev => ({ ...prev, client_id: null, client_name: '', client_phone: '', client_email: '' }));
    }
  };

  const handleNewClient = () => {
    setSelectedClient(null);
    setFormData(prev => ({ ...prev, client_id: null, client_name: clientInputValue, client_phone: '', client_email: '' }));
    setShowNewClientFields(true);
  };

  // ── Calendar event handlers ────────────────────────────────
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const props = event.extendedProps;
    if (props.isProcessing) {
      showSnackbar(`Processing time for ${props.client_name}`, 'info');
      return;
    }
    setSelectedEvent({ id: props.id, title: event.title, start: event.start, end: event.end, ...props });
    setViewDialogOpen(true);
  };

  const handleDateClick = (arg) => {
    const clickedDate = arg.date.toISOString().split('T')[0];
    const clickedTime = arg.date.toTimeString().substring(0, 5);
    handleOpenDialog(clickedDate, clickedTime);
  };

  // ── Appointment actions ────────────────────────────────────
  const handleEditAppointment = async () => {
    try {
      const response = await api.get(`/appointments/${selectedEvent.id}`);
      const appointment = response.data;
      setFormData({
        client_id: appointment.client_id || null,
        client_name: appointment.client_name,
        client_phone: appointment.client_phone || '',
        client_email: appointment.client_email || '',
        appointment_date: appointment.appointment_date,
        start_time: appointment.start_time,
        notes: appointment.notes || '',
        is_multi_service: appointment.is_multi_service || false,
        services: appointment.services || []
      });
      if (appointment.client_id) {
        try {
          const clientResponse = await api.get(`/clients/${appointment.client_id}`);
          setSelectedClient(clientResponse.data);
        } catch (e) { /* client may have been deleted */ }
      }
      setViewDialogOpen(false);
      setDialogOpen(true);
    } catch (error) {
      showSnackbar('Failed to load appointment details', 'error');
    }
  };

  const handleOpenJobCard = () => {
    setJobCardAppointment({
      id: selectedEvent.id,
      client_name: selectedEvent.client_name,
      service_name: selectedEvent.service_name ||
        (selectedEvent.services?.map(s => s.service_name).join(', ')) ||
        'Service',
      start_datetime: selectedEvent.start,
      total_price: selectedEvent.total_price || selectedEvent.price || 0
    });
    setViewDialogOpen(false);
    setJobCardOpen(true);
  };

  // Checkout — navigate to full till with booking ID
  const handleOpenCheckout = () => {
    setViewDialogOpen(false);
    navigate(`/solo/record-sale/${selectedEvent.id}`);
  };

  // Cancel — show confirmation first
  const handleCancelConfirm = () => {
    setCancelConfirmOpen(true);
  };

  const handleCancelAppointment = async () => {
    setCancelConfirmOpen(false);
    try {
      await api.post(`/appointments/${selectedEvent.id}/cancel`, {});
      showSnackbar('Appointment cancelled');
      setViewDialogOpen(false);
      fetchAppointments();
    } catch (error) {
      showSnackbar('Failed to cancel appointment', 'error');
    }
  };

  // Delete — window.confirm is fine here (it's delete, not cancel)
  const handleDeleteAppointment = async () => {
    if (!window.confirm('Are you sure you want to delete this appointment? This cannot be undone.')) return;
    try {
      await api.delete(`/appointments/${selectedEvent.id}`);
      showSnackbar('Appointment deleted');
      setViewDialogOpen(false);
      fetchAppointments();
    } catch (error) {
      showSnackbar('Failed to delete appointment', 'error');
    }
  };

  // ── Form handlers ──────────────────────────────────────────
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.client_name.trim()) errors.client_name = 'Client name is required';
    if (!formData.appointment_date) errors.appointment_date = 'Date is required';
    if (!formData.start_time) errors.start_time = 'Start time is required';
    if (formData.services.length === 0) errors.services = 'Please add at least one service';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (hasServiceRequiringPatchTest() && patchTestWarning && selectedClient?.patch_test_status !== 'valid') {
      if (!window.confirm(`Warning: ${patchTestWarning}\n\nDo you want to continue anyway?`)) return;
    }
    try {
      const dataToSubmit = {
        client_id: formData.client_id,
        client_name: formData.client_name,
        client_phone: formData.client_phone,
        client_email: formData.client_email,
        appointment_date: formData.appointment_date,
        start_time: formData.start_time,
        notes: formData.notes,
        is_multi_service: formData.is_multi_service,
        services: formData.services
      };
      if (selectedEvent?.id) {
        await api.put(`/appointments/${selectedEvent.id}`, dataToSubmit);
        showSnackbar('Appointment updated successfully!');
      } else {
        await api.post('/appointments', dataToSubmit);
        showSnackbar('Appointment created successfully!');
      }
      handleCloseDialog();
      fetchAppointments();
    } catch (error) {
      if (error.response?.status === 409) {
        showSnackbar('Time slot conflict! Please choose another time.', 'error');
      } else {
        showSnackbar(error.response?.data?.error || 'Failed to save appointment', 'error');
      }
    }
  };

  // ── RENDER ─────────────────────────────────────────────────
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>

      {/* Header */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight="600">Appointments</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchAppointments} size="small"><RefreshIcon /></IconButton>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>New</Button>
        </Box>
      </Box>

      {/* Calendar */}
      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Paper sx={{ height: '100%', p: 2 }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
            locale="en-GB"
            firstDay={1}
            slotDuration="00:15:00"
            slotLabelInterval="00:15:00"
            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            slotMinTime="07:00:00"
            slotMaxTime="22:00:00"
            allDaySlot={false}
            nowIndicator={false}
            height="auto"
            events={events}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            editable={false}
            selectable={true}
            businessHours={{ daysOfWeek: [1, 2, 3, 4, 5, 6], startTime: '09:00', endTime: '18:00' }}
            // Day-location V1 — shows the day's location label (if set)
            // under the day-of-week/date header. Same callback applies
            // across timeGridDay, timeGridWeek, and dayGridMonth, so this
            // covers all three views without extra per-view handling.
            dayHeaderContent={(arg) => {
              const key = DAY_KEYS[arg.date.getDay()];
              const loc = dayLocations[key];
              return (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {arg.text}
                  </Typography>
                  {loc && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {loc}
                    </Typography>
                  )}
                </Box>
              );
            }}
            datesSet={(dateInfo) => {
              const newRange = `${dateInfo.startStr}-${dateInfo.endStr}`;
              if (newRange !== currentDateRange) {
                setCurrentDateRange(newRange);
                fetchAppointments(dateInfo.start.toISOString(), dateInfo.end.toISOString());
              }
            }}
            loading={(isLoading) => setLoading(isLoading)}
          />
        </Paper>
      </Box>

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedEvent?.id ? 'Edit Appointment' : 'New Appointment'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>

            {/* Client selection */}
            <Grid item xs={12}>
              {!showNewClientFields ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={clientOptions}
                    getOptionLabel={(option) => option.full_name || ''}
                    value={selectedClient}
                    onChange={handleClientSelect}
                    inputValue={clientInputValue}
                    onInputChange={(event, newValue) => setClientInputValue(newValue)}
                    loading={clientLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Client"
                        placeholder="Start typing to search..."
                        error={!!formErrors.client_name}
                        helperText={formErrors.client_name || (clientInputValue.length >= 2 && clientOptions.length === 0 && !clientLoading ? 'No clients found — click "New Client" to add' : '')}
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {clientLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Box>
                            <Typography variant="body1">{option.full_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{option.phone || option.email || 'No contact info'}</Typography>
                          </Box>
                          {option.patch_test_status && option.patch_test_status !== 'valid' && (
                            <Chip label={option.patch_test_status} size="small" color={option.patch_test_status === 'none' ? 'default' : 'warning'} />
                          )}
                        </Box>
                      </li>
                    )}
                    noOptionsText={clientInputValue.length < 2 ? 'Type at least 2 characters to search' : 'No clients found'}
                  />
                  <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={handleNewClient} sx={{ height: 56, whiteSpace: 'nowrap' }}>
                    New Client
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="primary">New Client</Typography>
                    <Button size="small" onClick={() => setShowNewClientFields(false)}>Search existing instead</Button>
                  </Box>
                  <TextField fullWidth label="Client Name" name="client_name" value={formData.client_name} onChange={handleFormChange} error={!!formErrors.client_name} helperText={formErrors.client_name} required autoFocus />
                </Box>
              )}
            </Grid>

            {(showNewClientFields || selectedEvent?.id) && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone" name="client_phone" value={formData.client_phone} onChange={handleFormChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email" name="client_email" type="email" value={formData.client_email} onChange={handleFormChange} />
                </Grid>
              </>
            )}

            {patchTestWarning && (
              <Grid item xs={12}>
                <Alert severity={selectedClient?.patch_test_status === 'positive' ? 'error' : 'warning'} icon={<WarningIcon />}>
                  {patchTestWarning}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Date" name="appointment_date" type="date" value={formData.appointment_date} onChange={handleFormChange} error={!!formErrors.appointment_date} helperText={formErrors.appointment_date} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Start Time" name="start_time" type="time" value={formData.start_time} onChange={handleFormChange} error={!!formErrors.start_time} helperText={formErrors.start_time} InputLabelProps={{ shrink: true }} required />
            </Grid>

            {/* Services */}
            <Grid item xs={12}>
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Services</Typography>

                {formData.services.map((svc, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={1}><Typography variant="h6" color="primary">{index + 1}</Typography></Grid>
                      <Grid item xs={4}><Typography variant="body1" fontWeight="bold">{svc.service_name}</Typography></Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {svc.duration}min work{svc.processing_time > 0 && ` + ${svc.processing_time}min processing`}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}><Typography variant="body1">£{parseFloat(svc.price || 0).toFixed(2)}</Typography></Grid>
                      <Grid item xs={1}>
                        <IconButton size="small" color="error" onClick={() => removeService(index)}><DeleteIcon /></IconButton>
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Starts at: {calculateServiceStartTime(index)}
                    </Typography>
                  </Paper>
                ))}

                {formData.services.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Grid container>
                      <Grid item xs={6}><Typography variant="body2">Total Duration: {calculateTotalDuration()} minutes</Typography></Grid>
                      <Grid item xs={6} textAlign="right"><Typography variant="body2" fontWeight="bold">Total Price: £{calculateTotalPrice().toFixed(2)}</Typography></Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary">Appointment ends at: {calculateEndTime()}</Typography>
                  </Box>
                )}

                {formErrors.services && <Alert severity="error" sx={{ mt: 2 }}>{formErrors.services}</Alert>}

                <Box sx={{ mt: 2, p: 2, border: '2px dashed #ccc', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Add Service</Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Service</InputLabel>
                    <Select value={currentService.service_id} onChange={handleServiceSelect} label="Select Service">
                      {services.map((service) => (
                        <MenuItem key={service.id} value={service.id}>
                          {service.name} - £{service.default_price} ({service.duration}min)
                          {service.processing_time > 0 && ` + ${service.processing_time}min processing`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={addService} disabled={!currentService.service_id} fullWidth>
                    Add This Service
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleFormChange} multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{selectedEvent?.id ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── View / Appointment Details Dialog ── */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Appointment Details</Typography>
            <Chip label={selectedEvent?.status || 'scheduled'} color={getStatusColor(selectedEvent?.status)} size="small" />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Client</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedEvent.client_name}</Typography>

              {selectedEvent.client_phone && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedEvent.client_phone}</Typography>
                </>
              )}

              {selectedEvent.client_email && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedEvent.client_email}</Typography>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary">Services</Typography>
              {selectedEvent.is_multi_service && selectedEvent.services ? (
                <Box sx={{ mb: 2 }}>
                  {selectedEvent.services.map((svc, idx) => (
                    <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{idx + 1}. {svc.service_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {svc.duration}min{svc.processing_time > 0 && ` + ${svc.processing_time}min processing`}
                        {' - '}£{parseFloat(svc.price).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEvent.service_name || 'N/A'}</Typography>
              )}

              <Typography variant="subtitle2" color="text.secondary">Date & Time</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedEvent.appointment_date} at {selectedEvent.start_time}</Typography>

              <Typography variant="subtitle2" color="text.secondary">Price</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                £{parseFloat(selectedEvent.total_price || selectedEvent.price || 0).toFixed(2)}
              </Typography>

              {selectedEvent.notes && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedEvent.notes}</Typography>
                </>
              )}

              <RetailRecommendations serviceIds={selectedServiceIds} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Box>
            <IconButton onClick={handleDeleteAppointment} color="error" title="Delete appointment">
              <DeleteIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={handleOpenJobCard} startIcon={<ReceiptIcon />} variant="outlined" color="inherit" size="small">
              Job card
            </Button>
            {selectedEvent?.status === 'scheduled' && (
              <>
                {/* Renamed + wired to confirmation dialog */}
                <Button onClick={handleCancelConfirm} startIcon={<CloseIcon />}>
                  Cancel appointment
                </Button>
                <Button onClick={handleOpenCheckout} startIcon={<CheckIcon />} color="success" variant="contained">
                  Checkout
                </Button>
              </>
            )}
            <Button onClick={handleEditAppointment} startIcon={<EditIcon />} variant="outlined">
              Edit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Confirmation Dialog ── */}
      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel this appointment?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" fontWeight={500} sx={{ mb: 0.5 }}>
            {selectedEvent?.client_name}
            {selectedEvent?.service_name && ` · ${selectedEvent.service_name}`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedEvent?.appointment_date} at {selectedEvent?.start_time}
          </Typography>
          <Alert severity="warning">
            This cannot be undone. The client will not be automatically notified.
          </Alert>
        </DialogContent>
        <DialogActions>
          {/* Primary action is keep — prevent accidental cancel */}
          <Button onClick={() => setCancelConfirmOpen(false)} variant="contained">
            Keep appointment
          </Button>
          <Button onClick={handleCancelAppointment} color="error" variant="outlined">
            Yes, cancel appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Job Card Dialog ── */}
      <JobCard
        open={jobCardOpen}
        onClose={() => setJobCardOpen(false)}
        appointment={jobCardAppointment}
        onSaved={() => showSnackbar('Job card saved. Stock and shopping list updated.')}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Bookings;